import React from 'react';
import underline from '@assets/images/img/underline.svg';
import systemsScreenshot from '@assets/images/screenshots/systems.png';
import { PrimaryBtn } from '@components/Assets/Buttons';
import { SystemsFeature } from './SystemsFeature';
import { AnalyticsFeature } from './AnalyticsFeature';
import { useNavigate } from 'react-router-dom';
import { 
    ChartBarIcon, 
    BeakerIcon 
} from '@heroicons/react/24/outline';

const Hero = ({ isAuthenticated, isSubscribed }) => {
    const navigate = useNavigate();

    const handleAction = () => {
        if (!isAuthenticated) {
            navigate('/signup');
        } else if (!isSubscribed) {
            // Scroll to pricing section if on landing page
            const pricingSection = document.getElementById('pricing');
            if (pricingSection) {
                pricingSection.scrollIntoView({ behavior: 'smooth' });
            }
        } else {
            navigate('/systems');
        }
    };

    const scrollToSection = (sectionId) => {
        const section = document.getElementById(sectionId);
        if (section) {
            section.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const getButtonText = () => {
        if (!isAuthenticated) return 'Sign Up';
        if (!isSubscribed) return 'View Pricing';
        return 'Go to Systems';
    };

    return (
        <div className="bg-white dark:bg-brand-dark-900">
            {/* Hero section */}
            <div className="relative isolate overflow-hidden bg-white dark:bg-brand-dark-900 md:bg-brand-red-500 md:dark:bg-brand-red-500">
                {/* Top gradient fade */}
                <div className="absolute inset-x-0 top-0 -z-10 h-24 hidden md:block bg-gradient-to-b from-white dark:from-brand-dark-900 to-transparent sm:h-32" />

                <div className="mx-auto max-w-7xl pt-10 pb-24 sm:pb-32 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:px-8 lg:py-40">
                    <div className="px-6 lg:px-0 lg:pt-4">
                        <div className="max-w-lg mx-auto text-center sm:text-left">
                            {/* Main heading */}
                            <h3 className="mt-8 text-2xl font-semibold relative inline-block">
                                <span>Level Up Your Sports Betting.</span>
                            </h3>
                            
                            <h1 className="mt-4 text-4xl sm:text-6xl font-bold tracking-tight text-gray-900 dark:text-white">
                                FIND YOUR EDGE <span className="text-brand-red-500 dark:text-brand-red-500 relative inline-block">
                                    TODAY.
                                    <img src={underline} alt="" className="absolute -bottom-6 left-0 w-full" />
                                </span>
                            </h1>
                            
                            <p className="mt-8 text-lg font-medium text-pretty text-gray-600 dark:text-gray-400">
                                {isSubscribed 
                                    ? "Welcome back! Continue building and refining your sports betting systems."
                                    : "Transform your sports betting with powerful analytics and custom systems. Build winning strategies, analyze team performance, and make data-driven decisions — all in one platform."
                                }
                            </p>

                            {/* Feature Highlights */}
                            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 justify-items-center sm:justify-items-start">
                                <button 
                                    onClick={() => scrollToSection('systems-feature')}
                                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                >
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-red-500">
                                        <BeakerIcon className="h-5 w-5 text-white" />
                                    </div>
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">Custom Systems</span>
                                </button>
                                <button 
                                    onClick={() => scrollToSection('analytics-feature')}
                                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                >
                                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-red-500">
                                        <ChartBarIcon className="h-5 w-5 text-white" />
                                    </div>
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">Advanced Analytics</span>
                                </button>
                            </div>

                            {/* CTA Buttons */}
                            <div className="mt-10 flex items-center justify-center sm:justify-start gap-x-6">
                                <PrimaryBtn onClick={handleAction}>{getButtonText()}</PrimaryBtn>
                            </div>
                        </div>
                    </div>

                    {/* System Preview */}
                    <div className="mt-20 sm:mt-24 md:mx-auto lg:mx-0 lg:mt-0 lg:w-screen">
                        <div
                            className="absolute inset-y-0 right-1/2 -z-10 -mr-10 w-[200%] skew-x-[-30deg] bg-white dark:bg-brand-dark-900 shadow-xl shadow-brand-red-500/10 dark:shadow-brand-red-400/5 ring-1 ring-gray-200 dark:ring-gray-800 md:-mr-20 lg:-mr-36"
                            aria-hidden="true"
                        />
                        <div className="shadow-lg md:rounded-3xl">
                            <div className="bg-brand-dark-900 [clip-path:inset(0)] md:[clip-path:inset(0_round_theme(borderRadius.3xl))]">
                                <div
                                    className="absolute -inset-y-px left-1/2 -z-10 ml-10 md:ml-20 lg:ml-36"
                                    aria-hidden="true"
                                />
                                <div className="relative px-6 pb-8 pt-8 sm:pt-16 md:pl-16 md:pr-0">
                                    <div className="mx-auto max-w-2xl md:mx-0 md:max-w-none">
                                        <div className="w-screen overflow-hidden rounded-tl-xl bg-gray-900/70">
                                            <img 
                                                src={systemsScreenshot} 
                                                alt="Line Leaders Systems Interface" 
                                                className="w-auto h-auto object-cover"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="absolute inset-x-0 bottom-0 -z-10 h-24 hidden sm:block bg-gradient-to-t from-white dark:from-brand-dark-900 sm:h-32" />
            </div>
            <div id="systems-feature">
                <SystemsFeature />
            </div>
            <div id="analytics-feature">
                <AnalyticsFeature />
            </div>
        </div>
    );
};

export default Hero;